import { useReplicantSelector } from "@livcg/nodecg-react";
import type { ProjectState, ProjectsReplicant } from "@livcg/schema";
import { useCallback, useRef, useState } from "react";
import { sendRaw } from "./sendRaw.js";

const STORAGE_KEY = "livcg-selected-slug";

export type ActiveProject = {
  slug: string | null;
  state: ProjectState | null;
  /** True while we have a slug but the project entry isn't in the replicant
   *  yet — usually a millisecond race during boot. */
  isLoading: boolean;
  /** Switch the BROWSER-LOCAL selection. Persists to URL (?slug=) +
   *  localStorage + the server-global activeSlug hint. Other open clients
   *  are NOT moved — they keep their own local pick. Pass null to clear. */
  select: (slug: string | null) => void;
  /** Clears the selection in this browser only (does not write to the server). `keepUrl`
   *  — the ?slug= link is kept, only the remembered selection is forgotten. */
  clear: (opts?: { keepUrl?: boolean }) => void;
};

/** Initial browser-local desired slug: URL ?slug wins, then localStorage.
 *  Null means "no project" — the operator must pick one on the picker screen. */
function readInitialSlug(): string | null {
  try {
    const url = new URLSearchParams(window.location.search).get("slug");
    if (url) return url;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return stored;
  } catch {
    /* private mode / no window — ignore */
  }
  return null;
}

/**
 * A "signature" for swallowing the clock tick: on every write NodeCG rebuilds
 * the WHOLE value (`overlay`, `teams`, `lineup`… all become new objects), so
 * comparing by identity does not work — we compare by
 * value. The signature includes only the slug list and the SELECTED project;
 * `minute`/`second`/`updatedAt` are left out. As a result neither the per-second
 * tick nor writes in other projects cause a re-render.
 */
type Snapshot = { record: ProjectsReplicant | undefined; key: string };

function signature(record: ProjectsReplicant | undefined, slug: string | null): string {
  if (!record) return "";
  const slugs = Object.keys(record).sort().join(",");
  const project = slug ? record[slug] : undefined;
  if (!project) return slugs;
  const { match, ...rest } = project;
  const { minute: _m, second: _s, updatedAt: _u, ...restMatch } = match;
  return `${slugs}|${JSON.stringify(rest)}|${JSON.stringify(restMatch)}`;
}

function sameSnapshot(a: Snapshot, b: Snapshot): boolean {
  return a.key === b.key;
}

/** The "currently editing" project — BROWSER-LOCAL (SPEC B):
 *
 *    URL ?slug=  →  localStorage  →  null (the project picker screen)
 *
 *  There is no automatic fallback to the server `activeSlug` or to the first
 *  active project: otherwise a new operator would land on the on-air match
 *  someone else picked last. An archived/deleted selection also falls to null.
 *  `activeSlug` keeps being written on select as "last used". */
export function useActiveProject(): ActiveProject {
  const [localSlug, setLocalSlug] = useState<string | null>(readInitialSlug);

  /** So the signature knows which project it is computed for — the selection
   *  from the last render (the selector runs inside a ref, it is not a dependency). */
  const activeSlugRef = useRef<string | null>(null);

  // Subscribe to the whole projects record. The record ref changes on every
  // write (patchProject spreads a new object) and we derive slug/state in
  // render — this avoids stale-slice bugs when the local pick changes without
  // a replicant change firing.
  //
  // The clock tick (once a second, on the server side) bypasses this
  // subscription: the signature is computed without minute/second/updatedAt, i.e.
  // a write where only the time changed is treated as "unchanged" and the tree
  // does not re-render. Components that display the time subscribe separately
  // with `useLiveClock` (shared/liveClock.ts), while event writers take the live
  // value with `readLiveClock` at the moment of the press.
  const snapshot = useReplicantSelector<ProjectsReplicant, Snapshot>(
    "projects",
    (v) => ({ record: v, key: signature(v, activeSlugRef.current) }),
    sameSnapshot,
  );
  const record = snapshot.record;

  const effectiveSlug = localSlug && record?.[localSlug] ? localSlug : null;
  activeSlugRef.current = effectiveSlug;

  const state = effectiveSlug && record ? (record[effectiveSlug] ?? null) : null;

  const persistLocal = useCallback((slug: string | null) => {
    setLocalSlug(slug);
    try {
      if (slug) localStorage.setItem(STORAGE_KEY, slug);
      else localStorage.removeItem(STORAGE_KEY);
      const url = new URL(window.location.href);
      if (slug) url.searchParams.set("slug", slug);
      else url.searchParams.delete("slug");
      window.history.replaceState(null, "", url.toString());
    } catch {
      /* ignore */
    }
  }, []);

  const select = useCallback(
    (slug: string | null) => {
      persistLocal(slug);
      // Server-global "last used" hint. Other clients don't follow.
      void sendRaw("project.select", { slug });
    },
    [persistLocal],
  );

  const clear = useCallback(
    (opts?: { keepUrl?: boolean }) => {
      const urlSlug = opts?.keepUrl
        ? new URLSearchParams(window.location.search).get("slug")
        : null;
      persistLocal(urlSlug);
    },
    [persistLocal],
  );

  return {
    slug: effectiveSlug,
    state,
    isLoading: localSlug !== null && record === undefined,
    select,
    clear,
  };
}
